import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { Input } from "../../@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { api_endpoint } from "@/constants/constant";
import { useAuth } from "@clerk/clerk-react";
import { ClipLoader } from "react-spinners";

export default function BatchDeletePathwaysDialog({
  open,
  onOpenChange,
  pathways,
  onDeleted,
}) {
  const { getToken } = useAuth();
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  useEffect(() => {
    if (!open) return;
    setDeleting(false);
    setConfirmText("");
  }, [open]);

  const selected = useMemo(() => {
    const rows = Array.isArray(pathways) ? pathways : [];
    return rows.filter(Boolean);
  }, [pathways]);

  const pathwayIds = useMemo(() => {
    const ids = [];
    const seen = new Set();
    for (const p of selected) {
      const id = p?.id ?? p?.pathway_id ?? p?.pathwayId;
      const n = Number(id);
      if (!Number.isFinite(n)) continue;
      const key = String(n);
      if (seen.has(key)) continue;
      seen.add(key);
      ids.push(n);
    }
    return ids;
  }, [selected]);

  const selectedCount = selected.length;

  const canSubmit =
    !deleting &&
    selectedCount >= 2 &&
    pathwayIds.length >= 2 &&
    confirmText.trim().toUpperCase() === "DELETE";

  const handleDelete = async () => {
    if (selectedCount < 2) {
      toast.error("Select at least 2 pathways.");
      return;
    }

    if (pathwayIds.length < 2) {
      toast.error("Missing pathway ids for selected rows.");
      return;
    }

    if (confirmText.trim().toUpperCase() !== "DELETE") {
      toast.error("Type DELETE to confirm.");
      return;
    }

    setDeleting(true);
    try {
      const token = await getToken();
      if (!token) return;

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        data: {
          pathway_ids: pathwayIds,
        },
      };

      const res = await axios.delete(api_endpoint + "/pathway/bulk-delete", reqConfig);

      const data = res?.data;
      const totalDeleted =
        data?.total_deleted != null ? Number(data.total_deleted) : null;
      const totalErrors =
        data?.total_errors != null ? Number(data.total_errors) : null;

      const deletedCount = Number.isFinite(totalDeleted)
        ? totalDeleted
        : Array.isArray(data?.deleted_pathway_ids)
          ? data.deleted_pathway_ids.length
          : 0;

      if (Number.isFinite(totalErrors) && totalErrors > 0) {
        toast.success(
          `Deleted ${deletedCount} pathway${deletedCount === 1 ? "" : "s"}. ${totalErrors} failed.`
        );
      } else {
        toast.success(
          `Deleted ${deletedCount} pathway${deletedCount === 1 ? "" : "s"} successfully.`
        );
      }

      onOpenChange?.(false);
      onDeleted?.(data);
    } catch (error) {
      console.error("Failed to bulk delete pathways", error);
      toast.error("Failed to delete pathways. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const isDisabled = deleting;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md h-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-(--neutral-900)">
            Delete Pathways
          </DialogTitle>
          <DialogDescription className="font-body text-(--neutral-900)">
            Are you sure you want to delete <b>{selectedCount}</b> pathway{selectedCount === 1 ? "" : "s"}? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <p className="text-(--color-primary) font-bold mt-2">
          Warning: Deleting pathways will also affect all users assigned to them.
          <br />
          This may remove their access or progress.
        </p>

        <div className="mt-4">
          <label className="font-body text-sm text-(--neutral-900)">
            Type <b>DELETE</b> to confirm:
          </label>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder='Type "DELETE"'
            className="mt-1"
            disabled={isDisabled}
          />
        </div>

        <DialogFooter className="mt-4">
          <DialogClose asChild>
            <Button variant="outline" type="button" disabled={isDisabled}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="button"
            variant=""
            onClick={handleDelete}
            disabled={isDisabled || !canSubmit}
          >
            {deleting ? <ClipLoader size={18} color="#fff" /> : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
